import { Noun, Verb } from './analytics-constants';
import logTracking from './log-tracking';

/**
 * Defines the context in which the application is running
 */
export interface AppContext {
  schema: string;
  data: {
    client_version: string;
    client_locale: string;
  };
}

/**
 * The function used to retrieve execution context
 */
export type AppContextGetter = () => AppContext | AppContext[];

/**
 * An additional layer of context to describe the event, identifying the
 * specific entity affected. This rule improves reporting around event
 * distribution. We automatically get the user ID of the member doing the
 * event (by way of snowplow magic), but add the IDs of the associated
 * comment/card/board/other member if you can.
 */
export interface EventContext {
  [key: string]: string | number | boolean;
}

/**
 * An unstructured event.
 */
interface UnstructuredEvent {
  /**
   * High-level context for where an event occurs. Required.
   *
   * In the following example 'card detail' is the category:
   *
   * card detail: adds subscription to card by clicking subscribe button in sidebar
   */
  category: Noun;

  /**
   * Describes the action being performed. Required.
   *
   * In the following example 'adds' is the verb:
   *
   * card detail: adds subscription to card by clicking subscribe button in sidebar
   */
  verb: Verb;

  /**
   * Describes the entity on which the action is being performed,. Required.
   *
   * In the following example 'subscription' is the direct object:
   *
   * card detail: adds subscription to card by clicking subscribe button in sidebar
   */
  directObj: Noun;

  /**
   * An entity introduced by a preposition, likely related to the direct object.
   * We often put additional info in method, so only use this if method is
   * getting too bulky. You might not need this.
   *
   * In the following example 'with mention' is the prepositional object:
   *
   * home: adds comment with mention to card by clicking save on up next card
   */
  prepositionalObj?: string;

  /**
   * An entity indirectly affected by an action. You might not need this.
   *
   * In the following example 'to card' is the indirect object:
   *
   * card detail: adds subscription to card by clicking subscribe button in sidebar
   */
  indirectObj?: string;

  /**
   * The UI interaction and element used to create an action
   *
   * In the following example 'by clicking subscribe button in sidebar' is the indirect object:
   *
   * card detail: adds subscription to card by clicking subscribe button in sidebar
   */
  method?: string;

  /**
   * Any suppliemental information for this event
   */
  context?: EventContext | string;
}

/**
 * Module used to consistently fire analytics events throughout the application
 */
class Analytics {
  /**
   * Defines the context in which the application is running
   *
   * @type {AppContextGetter}
   * @memberof Analytics
   */
  getAppContext: AppContextGetter;

  /**
   * Method used to set the application context
   *
   * @param {AppContextGetter} getContext
   * @memberof Analytics
   */
  setAppContextGetter(getContext: AppContextGetter) {
    this.getAppContext = getContext;
  }

  /**
   * Method used to track unstructured events.
   *
   * Please see the following url if you have questions related to how to
   * properly create an event:
   *
   * http://go.atlassian.com/trello-event-tracking-intro
   *
   * @param {UnstructuredEvent} event
   * @memberof Analytics
   */
  trackUE(event: UnstructuredEvent): void {
    let contextString: string = '';

    if (typeof event.context === 'string') {
      contextString = event.context;
    } else if (
      event.context &&
      event.context.toString === Object.prototype.toString
    ) {
      contextString = JSON.stringify(event.context);
    }

    if (window.sp) {
      window.sp(
        'trackUnstructEvent:cf',
        {
          schema: 'iglu:com.trello/client-event/jsonschema/1-0-0',
          data: {
            ue_category: event.category,
            ue_verb: event.verb,
            ue_dir_object: event.directObj,
            ue_prep_object: event.prepositionalObj || '',
            ue_ind_object: event.indirectObj || '',
            ue_method: event.method || '',
            ue_context: contextString || '',
          },
        },
        this.getAppContext(),
      );

      logTracking({ ...event, context: contextString });
    }
  }

  trackPageview(fragment: string): void {
    if (window.ga) {
      window.ga('send', 'pageview', `/${fragment}`);
    }

    if (window.sp) {
      window.sp('trackPageView:cf', null, this.getAppContext());
    }
  }
}

export default Analytics;



// WEBPACK FOOTER //
// ./app/common/debug/analytics.ts